import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { appendUnique, firstPage, nextPage, pickerPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Button, Card, Cluster, EmptyState, Input, Select, Spinner, Stack, Tabs, Tooltip, useToast } from "@salesapp/ui";
import { opsApi, type Campaign, type Forecast, type ForecastAmount, type ForecastPerson, type ForecastTeam, type Invoice, type SupportTicket, type Territory } from "../../lib/opsApi";
import { RecordAuditInfo, stampUser } from "./RecordAuditInfo";
import { crmApi, type CommercialAgreement, type CommercialAgreementLine, type Order, type Product } from "../../lib/crmApi";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import {
  ACTION_HINTS,
  FIELD_HINTS,
  INVOICE_STATUS_OPTIONS,
  TICKET_STATUS_OPTIONS,
} from "../../help/fieldHints";
import { ListWorkspace, DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { CompanyLookup } from "./CompanyLookup";
import { SearchBar, StatusSlicer, statusLabel, useSelectOptions } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { ResponsiveDataTable } from "./ResponsiveDataTable";
import { useAuth } from "../../shell/AuthProvider";
import { useAccess } from "../../shell/AccessProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { isAgedToHistory, todayInZone } from "../../lib/localeFormat";
import { useCrmHref } from "../../lib/customerUrl";
import { ContactLookup } from "./ContactLookup";
import { CloseTicketModal } from "./CloseTicketModal";
import { MarkPaidDialog } from "./MarkPaidDialog";
import { SalesPlanPanel } from "./SalesPlanPage";
import { formatTicketAge, ticketAgeDays, ticketContactName, ticketNumberLabel } from "./supportAging";
import { dashboardDesignerApi, normalizeDashboardLayout, type DashboardTemplate } from "./dashboard_designer/api";
import { DashboardRenderer } from "./dashboard_designer/DashboardRenderer";
import "./dashboard_designer/dashboard_designer.css";

const SUPPORT_STATUSES = ["open", "closed"];

type SupportSortKey = "ticket_number" | "company_name" | "contact" | "status" | "opened_at" | "aging" | "lead";

function SortableHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  column: SupportSortKey;
  sortKey: SupportSortKey;
  sortDir: "asc" | "desc";
  onSort: (column: SupportSortKey) => void;
}) {
  const active = sortKey === column;
  return (
    <th aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="crm-sort-btn" onClick={() => onSort(column)}>
        {label}
      </button>
    </th>
  );
}

function compareTickets(
  a: SupportTicket,
  b: SupportTicket,
  sortKey: SupportSortKey,
  sortDir: "asc" | "desc",
): number {
  const dir = sortDir === "asc" ? 1 : -1;
  const text = (value: string | null | undefined) => (value ?? "").toLocaleLowerCase();
  switch (sortKey) {
    case "ticket_number":
      return dir * text(ticketNumberLabel(a)).localeCompare(text(ticketNumberLabel(b)));
    case "company_name":
      return dir * text(a.company_name).localeCompare(text(b.company_name));
    case "contact":
      return dir * text(ticketContactName(a)).localeCompare(text(ticketContactName(b)));
    case "status":
      return dir * text(a.status).localeCompare(text(b.status));
    case "opened_at":
      return dir * (Date.parse(a.opened_at || "") - Date.parse(b.opened_at || ""));
    case "aging":
      return dir * (ticketAgeDays(a) - ticketAgeDays(b));
    case "lead":
      return dir * text(a.generated_lead_code).localeCompare(text(b.generated_lead_code));
  }
}

function ResizableDescription({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="crm-resizable-field">
      <label className="crm-field" htmlFor={id}>Issue description</label>
      <div className="crm-resizable-field-shell">
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Describe the issue"
        />
      </div>
      <p className="crm-resizable-field-hint">Drag the handle at the bottom-left to make more room for typing.</p>
    </div>
  );
}

export function SupportPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [companyId, setCompanyId] = useState(searchParams.get("companyId") ?? "");
  const [companyName, setCompanyName] = useState(searchParams.get("companyName") ?? "");
  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [notifyUser, setNotifyUser] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(searchParams.get("new") === "1");
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SupportSortKey>("opened_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [closingTicket, setClosingTicket] = useState<SupportTicket | null>(null);
  const [closeComment, setCloseComment] = useState("");
  const [closeNotify, setCloseNotify] = useState(false);
  const [closing, setClosing] = useState(false);
  const statuses = useSelectOptions(orgId, "support", "status", SUPPORT_STATUSES);

  const load = useCallback(async () => {
    try {
      setTickets(
        (
          await opsApi.listTickets(orgId, {
            status: statusFilter || undefined,
            q: search || undefined,
          })
        ).tickets,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tickets could not be loaded.");
    }
  }, [orgId, search, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedTickets = useMemo(
    () => [...tickets].sort((a, b) => compareTickets(a, b, sortKey, sortDir)),
    [tickets, sortKey, sortDir],
  );

  function toggleSort(column: SupportSortKey) {
    if (sortKey === column) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column);
    setSortDir(column === "opened_at" || column === "aging" ? "desc" : "asc");
  }

  function resetForm() {
    setTitle("");
    setDescription("");
    setContactId("");
    setContactName("");
    setContactEmail("");
    setNotifyUser(false);
    if (!searchParams.get("companyId")) {
      setCompanyId("");
      setCompanyName("");
    }
    setShowForm(false);
    const params = new URLSearchParams(searchParams);
    params.delete("new");
    setSearchParams(params, { replace: true });
  }

  async function create() {
    setError(null);
    try {
      await opsApi.createTicket(orgId, {
        company_id: companyId,
        contact_id: contactId,
        title,
        description,
        notify_user: notifyUser,
      });
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ticket could not be created.");
    }
  }

  async function confirmClose() {
    if (!closingTicket || !closeComment.trim()) return;
    setClosing(true);
    setError(null);
    try {
      await opsApi.patchTicket(orgId, closingTicket.id, {
        status: "closed",
        comment: closeComment.trim(),
        notify_user: closeNotify,
      });
      setClosingTicket(null);
      setCloseComment("");
      setCloseNotify(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ticket could not be closed.");
    } finally {
      setClosing(false);
    }
  }

  const canCreate = Boolean(contactId && companyId && title.trim() && description.trim());
  const canNotifyCreate = Boolean(contactEmail);

  return (
    <ListWorkspace
      title="Customer support"
      actions={
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? "Cancel" : "New ticket"}</Button>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search support cases…"
            label="Search support cases"
            hint="Search by ticket number, title, description, contact, company, or status."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={statuses}
            label="Filter by case status"
            optionHints={TICKET_STATUS_OPTIONS}
            title={ACTION_HINTS.filter_ticket_status}
          />
        </div>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <h2 className="crm-section-title">New support ticket</h2>
            <ContactLookup
              orgId={orgId}
              companyId={companyId || undefined}
              value={contactName}
              contactId={contactId}
              onChange={({ name, contactId: nextId, companyId: nextCompanyId, companyName: nextCompanyName, email }) => {
                setContactName(name);
                setContactId(nextId);
                setContactEmail(email);
                if (nextId) {
                  setCompanyId(nextCompanyId);
                  setCompanyName(nextCompanyName);
                } else if (!searchParams.get("companyId")) {
                  setCompanyId("");
                  setCompanyName("");
                }
              }}
            />
            <Input label="Company" value={companyName} readOnly placeholder="Filled from the contact" />
            <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Title" />
            <ResizableDescription id="support-description" value={description} onChange={setDescription} />
            <label className="crm-check">
              <input
                type="checkbox"
                checked={notifyUser}
                disabled={!canNotifyCreate}
                onChange={(event) => setNotifyUser(event.target.checked)}
              />
              Notify the contact by email
            </label>
            {!canNotifyCreate && contactId ? (
              <p className="crm-muted">This contact has no email address to notify.</p>
            ) : null}
            <div className="crm-form-actions">
              <Button onClick={() => create()} disabled={!canCreate}>
                Log ticket
              </Button>
            </div>
          </Stack>
        </Card>
      )}
      {tickets.length === 0 ? (
        <Card>
          <EmptyState title="No support tickets" description="Log a request from a company record or with New ticket." />
        </Card>
      ) : (
        <Card className="crm-table-card">
          <table className="crm-table">
            <thead>
              <tr>
                <SortableHeader label="Ticket Number" column="ticket_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Company" column="company_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Contact" column="contact" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Status" column="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Opened" column="opened_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Aging" column="aging" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Lead" column="lead" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="crm-table-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td>
                    <Link to={crmHref(`/support/${ticket.id}`)}>{ticketNumberLabel(ticket)}</Link>
                  </td>
                  <td>
                    {ticket.company_id ? (
                      <Link to={crmHref(`/companies/${ticket.company_id}`)}>{ticket.company_name}</Link>
                    ) : (
                      ticket.company_name
                    )}
                  </td>
                  <td>{ticketContactName(ticket) || "—"}</td>
                  <td>
                    <StatusBadge status={ticket.status} options={TICKET_STATUS_OPTIONS} />
                  </td>
                  <td>{ticket.opened_at ? formatDate(ticket.opened_at) : "—"}</td>
                  <td title={FIELD_HINTS.ticket_aging}>{formatTicketAge(ticketAgeDays(ticket))}</td>
                  <td>
                    {ticket.generated_lead_id ? (
                      <Link to={crmHref(`/leads/${ticket.generated_lead_id}`)}>{ticket.generated_lead_code ?? "Lead"}</Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="crm-table-actions">
                    <Cluster gap={2}>
                      {!ticket.generated_lead_id && (
                        <Button
                          variant="secondary"
                          title={ACTION_HINTS.generate_lead}
                          onClick={() => navigate(crmHref(`/support/${ticket.id}?qualify=1`))}
                        >
                          Generate lead
                        </Button>
                      )}
                      {ticket.status === "open" && (
                        <Button
                          variant="secondary"
                          title={ACTION_HINTS.close_ticket}
                          onClick={() => {
                            setClosingTicket(ticket);
                            setCloseComment("");
                            setCloseNotify(false);
                          }}
                        >
                          Close
                        </Button>
                      )}
                      <DeleteRecordButton
                        capability="crm.support"
                          recordLabel={ticketNumberLabel(ticket)}
                        size="sm"
                        variant="ghost"
                        onDeleted={async () => {
                          await opsApi.deleteTicket(orgId, ticket.id);
                          await load();
                        }}
                      />
                    </Cluster>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <CloseTicketModal
        open={Boolean(closingTicket)}
        comment={closeComment}
        notifyUser={closeNotify}
        canNotify={Boolean(closingTicket?.contact_email)}
        onCommentChange={setCloseComment}
        onNotifyChange={setCloseNotify}
        onCancel={() => setClosingTicket(null)}
        onConfirm={() => confirmClose()}
        saving={closing}
      />
    </ListWorkspace>
  );
}

export function InvoicesPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderId, setOrderId] = useState("");
  const [orderText, setOrderText] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { formatDate, formatMoney, historyAfterDays, timezone } = useLocale();
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const companyCell = (invoice: Invoice) =>
    invoice.company_id ? <Link to={crmHref(`/companies/${invoice.company_id}`)}>{invoice.company_name}</Link> : invoice.company_name;

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const invoiceData = await opsApi.listInvoices(orgId, search || undefined, nextPage(invoices.length));
      const page = trimPage(invoiceData.invoices);
      setInvoices((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(invoiceData.total ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load more invoices.");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [invoiceData, orderData] = await Promise.all([opsApi.listInvoices(orgId, search || undefined, firstPage()), crmApi.listOrders(orgId)]);
      const page = trimPage(invoiceData.invoices);
      setInvoices(page.items);
      setHasMore(page.hasMore);
      setTotal(invoiceData.total ?? null);
      setOrders(orderData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invoices could not be loaded.");
    }
  }, [orgId, search]);

  useEffect(() => {
    void load();
  }, [load]);

  // A paid invoice stays in the open list for the org's "move to history after N days" setting, then
  // drops into History. Unpaid (and void) invoices are always open work.
  const isHistory = (invoice: Invoice) =>
    invoice.status === "paid" && isAgedToHistory(invoice.paid_at ?? invoice.payment_date, historyAfterDays);
  const visibleInvoices = statusFilter ? invoices.filter((invoice) => invoice.status === statusFilter) : invoices;
  const openInvoices = visibleInvoices.filter((invoice) => !isHistory(invoice));
  const historyInvoices = visibleInvoices.filter(isHistory);

  return (
    <ListWorkspace
      title="Invoices"
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search invoices…"
            label="Search invoices"
            hint="Search by invoice number, order number, company, status, or payment reference."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={INVOICE_STATUS_OPTIONS.map((o) => o.label.toLowerCase())}
            label="Filter by invoice status"
            optionHints={INVOICE_STATUS_OPTIONS}
          />
        </div>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {payingInvoice && (
        <MarkPaidDialog
          invoiceNumber={payingInvoice.invoice_number}
          today={todayInZone(timezone)}
          onClose={() => setPayingInvoice(null)}
          onConfirm={async (body) => {
            await opsApi.payInvoice(orgId, payingInvoice.id, body);
            await load();
          }}
        />
      )}
      <Cluster gap={2} align="end" className="crm-invoice-generate">
        <div className="crm-invoice-order-picker">
          <Input
            list="crm-invoice-order-options"
            value={orderText}
            onChange={(event) => {
              const text = event.target.value;
              setOrderText(text);
              const match = orders.find((order) => (order.order_number ?? "").toLowerCase() === text.trim().toLowerCase());
              setOrderId(match?.id ?? "");
            }}
            aria-label="Order"
            title={ACTION_HINTS.choose_order}
            label="Order"
            tooltip={ACTION_HINTS.choose_order}
            placeholder="Search or choose an order"
            autoComplete="off"
          />
          <datalist id="crm-invoice-order-options">
            {orders.map((order) => (
              <option key={order.id} value={order.order_number ?? ""}>{order.company_name ?? ""}</option>
            ))}
          </datalist>
        </div>
        <Button
          size="sm"
          title={ACTION_HINTS.generate_invoice}
          onClick={() =>
            opsApi.createInvoice(orgId, orderId).then(() => {
              setOrderId("");
              setOrderText("");
              return load();
            })
          }
          disabled={!orderId}
        >
          Generate invoice
        </Button>
      </Cluster>
      <table className="crm-table crm-invoice-table" aria-label="Open invoices">
        <colgroup>
          <col className="crm-invoice-col-number" />
          <col className="crm-invoice-col-created" />
          <col className="crm-invoice-col-order" />
          <col className="crm-invoice-col-company" />
          <col className="crm-invoice-col-total" />
          <col className="crm-invoice-col-status" />
          <col className="crm-invoice-col-paid" />
          <col className="crm-invoice-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th title={FIELD_HINTS.invoice_number}>Number</th>
            <th title="Date this invoice was created">Created</th>
            <th title="The order this invoice was generated from">Order</th>
            <th title={FIELD_HINTS.opportunity_company}>Company</th>
            <th title={FIELD_HINTS.invoice_total}>Total</th>
            <th title={FIELD_HINTS.invoice_status}>Status</th>
            <th title="When this invoice was paid">Paid</th>
            <th className="crm-table-actions-col" title="Row actions for this invoice">Actions</th>
          </tr>
        </thead>
        <tbody>
          {openInvoices.map((invoice) => (
            <tr key={invoice.id}>
              <td><Link to={crmHref(`/invoices/${invoice.id}`)}>{invoice.invoice_number}</Link></td>
              <td>{invoice.created_at ? formatDate(invoice.created_at) : "—"}</td>
              <td>{invoice.order_number ?? "—"}</td>
              <td>{companyCell(invoice)}</td>
              <td>{formatMoney(invoice.total, invoice.currency)}</td>
              <td>
                <StatusBadge status={invoice.status} options={INVOICE_STATUS_OPTIONS} />
              </td>
              <td>{invoice.paid_at ? formatDate(invoice.paid_at) : "—"}</td>
              <td className="crm-table-actions">
                <ResponsiveActions
                  primary={
                    invoice.status === "issued" ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        title={ACTION_HINTS.mark_paid}
                        onClick={() => setPayingInvoice(invoice)}
                      >
                        Mark paid
                      </Button>
                    ) : invoice.status === "paid" ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        title={ACTION_HINTS.undo_paid}
                        onClick={() => opsApi.unpayInvoice(orgId, invoice.id).then(load)}
                      >
                        Undo paid
                      </Button>
                    ) : (
                      <Link to={crmHref(`/invoices/${invoice.id}`)} title={ACTION_HINTS.view_record}>
                        View
                      </Link>
                    )
                  }
                  more={
                    <>
                      {invoice.status === "issued" || invoice.status === "paid" ? (
                        <Link to={crmHref(`/invoices/${invoice.id}`)} title={ACTION_HINTS.view_record}>
                          View
                        </Link>
                      ) : null}
                      {invoice.status !== "paid" && (
                        <DeleteRecordButton
                          capability="crm.invoices"
                          recordLabel={invoice.invoice_number}
                          size="sm"
                          variant="ghost"
                          onDeleted={async () => {
                            await opsApi.deleteInvoice(orgId, invoice.id);
                            await load();
                          }}
                        />
                      )}
                    </>
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {historyInvoices.length > 0 && (
        <>
          <h2 className="crm-section-title">Invoice history</h2>
          <p className="crm-help">Invoices paid more than {historyAfterDays} day(s) ago.</p>
          <table className="crm-table crm-invoice-table" aria-label="Invoice history">
            <colgroup>
              <col className="crm-invoice-col-number" />
              <col className="crm-invoice-col-created" />
              <col className="crm-invoice-col-order" />
              <col className="crm-invoice-col-company" />
              <col className="crm-invoice-col-total" />
              <col className="crm-invoice-col-status" />
              <col className="crm-invoice-col-paid" />
              <col className="crm-invoice-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th title={FIELD_HINTS.invoice_number}>Number</th>
                <th title="Date this invoice was created">Created</th>
                <th title="The order this invoice was generated from">Order</th>
                <th title={FIELD_HINTS.opportunity_company}>Company</th>
                <th title={FIELD_HINTS.invoice_total}>Total</th>
                <th title={FIELD_HINTS.invoice_status}>Status</th>
                <th title="When this invoice was paid">Paid</th>
                <th className="crm-table-actions-col" title="Row actions for this invoice">Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td><Link to={crmHref(`/invoices/${invoice.id}`)}>{invoice.invoice_number}</Link></td>
                  <td>{invoice.created_at ? formatDate(invoice.created_at) : "—"}</td>
                  <td>{invoice.order_number ?? "—"}</td>
                  <td>{companyCell(invoice)}</td>
                  <td>{formatMoney(invoice.total, invoice.currency)}</td>
                  <td><StatusBadge status={invoice.status} options={INVOICE_STATUS_OPTIONS} /></td>
                  <td>{invoice.paid_at ? formatDate(invoice.paid_at) : "—"}</td>
                  <td className="crm-table-actions">
                    <Link to={crmHref(`/invoices/${invoice.id}`)} title={ACTION_HINTS.view_record}>
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <LoadMore shown={invoices.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="invoices" />
    </ListWorkspace>
  );
}

const AGREEMENT_STATUSES = ["draft", "active", "expired", "cancelled"];
const AGREEMENT_STATUS_HINT = "Only Active agreements apply their contracted prices, lead times and terms to quotes and orders.";

/** The API rejects unknown keys, so send only the writable line fields (not id, product_name, timestamps...). */
function agreementLinePayload(line: CommercialAgreementLine): CommercialAgreementLine {
  return {
    ...(line.id ? { id: line.id } : {}),
    product_id: line.product_id,
    contracted_unit_price: line.contracted_unit_price ?? null,
    discount_pct: line.discount_pct ?? null,
    tax_exempt: line.tax_exempt ?? false,
    currency: line.currency ?? null,
    unit_of_measure: line.unit_of_measure || "each",
    min_qty: line.min_qty ?? null,
    max_qty: line.max_qty ?? null,
    lead_time_days: line.lead_time_days ?? null,
    effective_from: line.effective_from ? line.effective_from.slice(0, 10) : null,
    effective_to: line.effective_to ? line.effective_to.slice(0, 10) : null,
  };
}

function validateAgreement(startDate: string, endDate: string, lines: CommercialAgreementLine[]): string | null {
  if (startDate && endDate && endDate < startDate) return "End date cannot be before the start date.";
  for (const [index, line] of lines.entries()) {
    const n = index + 1;
    const hasPrice = line.contracted_unit_price != null;
    const hasDiscount = line.discount_pct != null;
    if (hasPrice === hasDiscount) return `Line ${n}: enter either a unit price or a discount %, not both or neither.`;
    if (hasPrice && (line.contracted_unit_price as number) < 0) return `Line ${n}: unit price cannot be negative.`;
    if (hasDiscount && ((line.discount_pct as number) < 0 || (line.discount_pct as number) > 100)) {
      return `Line ${n}: discount must be between 0 and 100.`;
    }
    if (line.min_qty != null && line.max_qty != null && line.max_qty < line.min_qty) {
      return `Line ${n}: maximum quantity cannot be lower than minimum quantity.`;
    }
    if (line.lead_time_days != null && line.lead_time_days < 0) return `Line ${n}: lead time cannot be negative.`;
  }
  return null;
}

/** A product dropdown that still shows a product the catalogue list no longer returns, instead of silently showing the first option. */
function productOptionsFor(products: Product[], line: CommercialAgreementLine): Product[] {
  if (products.some((product) => product.id === line.product_id)) return products;
  return [{ id: line.product_id, name: line.product_name ?? "Unavailable product" } as Product, ...products];
}

const EMPTY_AGREEMENT_FORM = {
  status: "draft",
  start_date: "",
  end_date: "",
  payment_terms: "",
  delivery_terms: "",
  notes: "",
  lines: [] as CommercialAgreementLine[],
};

export function AgreementsPage({ orgId }: { orgId: string }) {
  const crmHref = useCrmHref();
  const { show } = useToast();
  const [agreements, setAgreements] = useState<CommercialAgreement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_AGREEMENT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const [agreementRows, productRows] = await Promise.all([
        crmApi.listAgreements(orgId),
        crmApi.listProducts(orgId, undefined, pickerPage()),
      ]);
      setAgreements(agreementRows);
      setProducts(productRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Agreements could not be loaded.");
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  function addLine() {
    const first = products[0];
    if (!first) return;
    setForm((current) => ({
      ...current,
      lines: [
        ...current.lines,
        {
          product_id: first.id,
          contracted_unit_price: first.unit_price ?? null,
          discount_pct: null,
          unit_of_measure: first.unit_of_measure ?? "each",
          min_qty: null,
          max_qty: null,
          lead_time_days: null,
        },
      ],
    }));
  }

  async function save() {
    if (!companyId || form.lines.length === 0) return;
    const invalid = validateAgreement(form.start_date, form.end_date, form.lines);
    if (invalid) {
      setError(reportSaveBlocked(show, invalid));
      return;
    }
    setError(null);
    try {
      await crmApi.createAgreement(
        {
          company_id: companyId,
          status: form.status,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          payment_terms: form.payment_terms || null,
          delivery_terms: form.delivery_terms || null,
          notes: form.notes || null,
          lines: form.lines.map(agreementLinePayload),
        },
        orgId,
      );
      setShowForm(false);
      setForm(EMPTY_AGREEMENT_FORM);
      setCompanyId("");
      setCompanyName("");
      reportSaved(show);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Agreement could not be saved."));
    }
  }

  const visibleAgreements = agreements.filter((agreement) => {
    if (statusFilter && (agreement.status ?? "draft") !== statusFilter) return false;
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    const haystack = [
      agreement.agreement_number,
      agreement.company_name,
      agreement.status,
      agreement.territory_name,
      agreement.payment_terms,
      agreement.delivery_terms,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
  const statusOptions = Array.from(
    new Set(agreements.map((agreement) => agreement.status ?? "draft")),
  ).sort();
  const territoryOptions = Array.from(
    new Set(agreements.map((agreement) => agreement.territory_name ?? "Global")),
  ).sort();

  return (
    <ListWorkspace
      title="Commercial agreements"
      actions={
        <Button onClick={() => setShowForm((value) => !value)}>{showForm ? "Cancel" : "New agreement"}</Button>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search agreements..."
            label="Search agreements"
            hint="Search by agreement number, company, status, territory, or terms."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={AGREEMENT_STATUSES}
            label="Filter by agreement status"
            title={AGREEMENT_STATUS_HINT}
          />
        </div>
      }
    >
      <p className="crm-help">Commercial agreements are a company's price book for a territory. Lines reference the shared product catalogue — contracted prices snapshot onto quotes and orders at add time. {AGREEMENT_STATUS_HINT}</p>
      {error && <p className="crm-error">{error}</p>}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <h2 className="crm-section-title">New commercial agreement</h2>
            <CompanyLookup
              orgId={orgId}
              value={companyName}
              companyId={companyId}
              onChange={({ name, companyId: nextId }) => {
                setCompanyName(name);
                setCompanyId(nextId);
              }}
            />
            {companyId && agreements.some(
              (item) => item.company_id === companyId && (item.status === "active" || item.status === "draft"),
            ) ? (
              <p className="crm-warning">
                This company already has an active or draft agreement. You can still create another.
              </p>
            ) : null}
            <Select label="Status" tooltip={AGREEMENT_STATUS_HINT} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            <div className="crm-field-grid">
              <Input label="Start date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <Input label="End date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <Input label="Payment terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
            <Input label="Delivery terms" value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} />
            <DetailSection title="Agreement lines">
              <table className="crm-table crm-agreement-lines-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Unit price</th>
                    <th>Currency</th>
                    <th>Discount %</th>
                    <th>UOM</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Lead time</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, index) => (
                    <tr key={index}>
                      <td>
                        <select
                          className="crm-select"
                          value={line.product_id}
                          onChange={(e) => {
                            const product = products.find((item) => item.id === e.target.value);
                            setForm((current) => ({
                              ...current,
                              lines: current.lines.map((row, rowIndex) =>
                                rowIndex === index
                                  ? {
                                      ...row,
                                      product_id: e.target.value,
                                      contracted_unit_price: product?.unit_price ?? row.contracted_unit_price,
                                      currency: product?.currency ?? row.currency,
                                      unit_of_measure: product?.unit_of_measure ?? row.unit_of_measure ?? "each",
                                    }
                                  : row,
                              ),
                            }));
                          }}
                        >
                          {productOptionsFor(products, line).map((product) => (
                            <option key={product.id} value={product.id}>{product.name}</option>
                          ))}
                        </select>
                      </td>
                      <td><Input type="number" value={String(line.contracted_unit_price ?? "")} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, contracted_unit_price: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                      <td><Input aria-label="Line currency" value={line.currency ?? ""} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, currency: e.target.value.toUpperCase() } : row) }))} /></td>
                      <td><Input type="number" value={String(line.discount_pct ?? "")} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, discount_pct: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                      <td><Input value={line.unit_of_measure ?? "each"} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, unit_of_measure: e.target.value } : row) }))} /></td>
                      <td><Input type="number" value={String(line.min_qty ?? "")} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, min_qty: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                      <td><Input type="number" value={String(line.max_qty ?? "")} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, max_qty: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                      <td><Input type="number" value={String(line.lead_time_days ?? "")} onChange={(e) => setForm((current) => ({ ...current, lines: current.lines.map((row, rowIndex) => rowIndex === index ? { ...row, lead_time_days: e.target.value ? Number(e.target.value) : null } : row) }))} /></td>
                      <td><Button variant="ghost" size="sm" onClick={() => setForm((current) => ({ ...current, lines: current.lines.filter((_, rowIndex) => rowIndex !== index) }))}>Remove</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Cluster className="crm-agreement-lines-actions" gap={2}>
                <Button variant="secondary" onClick={addLine} disabled={products.length === 0}>Add line</Button>
                <Button onClick={() => save()} disabled={!companyId || form.lines.length === 0}>Save agreement</Button>
              </Cluster>
            </DetailSection>
          </Stack>
        </Card>
      )}
      {agreements.length === 0 ? (
        <Card>
          <EmptyState title="No agreements" description="Create a company's price book with New agreement." />
        </Card>
      ) : visibleAgreements.length === 0 ? (
        <Card>
          <EmptyState title="No matching agreements" description="Try a different search or filter." />
        </Card>
      ) : (
        <Card className="crm-table-card">
          <ResponsiveDataTable
            caption="Commercial agreements"
            columns={[
              { key: "agreement", label: "Agreement", primary: true, hint: "Agreement number. Open it to edit the price book lines." },
              {
                key: "company",
                label: "Company",
                hint: "Company whose contracted prices this agreement holds.",
                primary: true,
                sortable: true,
                sortValue: (row) => row.cells.company_sort as string,
              },
              {
                key: "status",
                label: "Status",
                hint: AGREEMENT_STATUS_HINT,
                primary: true,
                sortable: true,
                sortValue: (row) => row.cells.status_sort as string,
                filterOptions: statusOptions.map(statusLabel),
                filterValue: (row) => statusLabel(row.cells.status_sort as string),
              },
              {
                key: "territory",
                label: "Territory",
                hint: "Sales territory this agreement applies to.",
                sortable: true,
                filterOptions: territoryOptions,
              },
              {
                key: "dates",
                label: "Dates",
                hint: "Start and end date the agreement is in force.",
                sortable: true,
                sortValue: (row) => row.cells.dates_sort as string,
              },
              { key: "terms", label: "Terms", hint: "Payment or delivery terms applied to quotes and orders." },
            ]}
            rows={visibleAgreements.map((agreement) => ({
              id: agreement.id,
              cells: {
                agreement: (
                  <Link to={crmHref(`/agreements/${agreement.id}`)}>{agreement.agreement_number}</Link>
                ),
                company: agreement.company_id ? (
                  <Link to={crmHref(`/companies/${agreement.company_id}`)}>{agreement.company_name}</Link>
                ) : (
                  agreement.company_name
                ),
                company_sort: agreement.company_name ?? "",
                status: <StatusBadge status={agreement.status ?? "draft"} />,
                status_sort: agreement.status ?? "draft",
                territory: agreement.territory_name ?? "Global",
                dates: [agreement.start_date, agreement.end_date].filter(Boolean).join(" - ") || "-",
                dates_sort: agreement.start_date ?? "",
                terms: agreement.payment_terms || agreement.delivery_terms || "-",
              },
              actions: (
                <DeleteRecordButton
                  capability="crm.agreements"
                  recordLabel={agreement.agreement_number || "this agreement"}
                  size="sm"
                  variant="ghost"
                  onDeleted={async () => {
                    try {
                      await crmApi.deleteAgreement(agreement.id, orgId);
                      await load();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Agreement could not be deleted.");
                    }
                  }}
                />
              ),
            }))}
          />
        </Card>
      )}
    </ListWorkspace>
  );
}

export function AgreementDetailPage({ orgId }: { orgId: string }) {
  const { agreementId = "" } = useParams();
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const { show } = useToast();
  const [agreement, setAgreement] = useState<CommercialAgreement | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("draft");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [deliveryTerms, setDeliveryTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<CommercialAgreementLine[]>([]);
  const [territoryId, setTerritoryId] = useState("");
  const [territories, setTerritories] = useState<Territory[]>([]);
  const { can: canAccess } = useAccess();
  const canAssignTerritory = canAccess("crm.territories");
  const { formatDateTime } = useLocale();

  const load = useCallback(async () => {
    if (!agreementId) return;
    setLoading(true);
    setError(null);
    try {
      const [data, productRows, territoryRows] = await Promise.all([
        crmApi.getAgreement(agreementId, orgId),
        crmApi.listProducts(orgId, undefined, pickerPage()),
        opsApi.listTerritories(orgId).then((row) => row.territories).catch(() => [] as Territory[]),
      ]);
      setAgreement(data);
      setProducts(productRows);
      setTerritories(territoryRows);
      setTerritoryId(data.territory_id ?? "");
      setStatus(data.status ?? "draft");
      setStartDate(data.start_date?.slice(0, 10) ?? "");
      setEndDate(data.end_date?.slice(0, 10) ?? "");
      setPaymentTerms(data.payment_terms ?? "");
      setDeliveryTerms(data.delivery_terms ?? "");
      setNotes(data.notes ?? "");
      setLines(data.lines ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Agreement could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [agreementId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  function addLine() {
    const first = products[0];
    if (!first) return;
    setLines((current) => [
      ...current,
      {
        product_id: first.id,
        contracted_unit_price: first.unit_price ?? null,
        discount_pct: null,
        unit_of_measure: first.unit_of_measure ?? "each",
        min_qty: null,
        max_qty: null,
        lead_time_days: null,
      },
    ]);
  }

  async function save() {
    if (!agreement) return;
    const invalid = validateAgreement(startDate, endDate, lines);
    if (invalid) {
      setError(reportSaveBlocked(show, invalid));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await crmApi.updateAgreement(
        agreement.id,
        {
          company_id: agreement.company_id,
          territory_id: canAssignTerritory ? territoryId || agreement.territory_id : agreement.territory_id,
          status,
          start_date: startDate || null,
          end_date: endDate || null,
          payment_terms: paymentTerms || null,
          delivery_terms: deliveryTerms || null,
          notes: notes || null,
          lines: lines.map(agreementLinePayload),
        },
        orgId,
      );
      show("Saved!", "success");
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Agreement could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Card>Loading agreement…</Card>;
  if (error && !agreement) return <Card className="crm-error">{error}</Card>;
  if (!agreement) return null;

  return (
    <RecordWorkspace
      kind="agreement"
      objectLabel="Agreement"
      title={agreement.agreement_number || "Agreement"}
      subtitle={agreement.company_name}
      backTo={crmHref("/agreements")}
      backLabel="← Back to Agreements"
      highlights={[
        { label: "Status", value: <StatusBadge status={status} /> },
        { label: "Company", value: agreement.company_id ? <Link to={crmHref(`/companies/${agreement.company_id}`)}>{agreement.company_name}</Link> : agreement.company_name },
        {
          label: "Territory",
          value: (
            <Tooltip text="Sales territory this agreement's price book applies to. Plans with territories can change it in the Territory field below.">
              <span>{agreement.territory_name ?? "Global"}</span>
            </Tooltip>
          ),
        },
        { label: "Dates", value: [startDate, endDate].filter(Boolean).join(" - ") || "-" },
      ]}
      actions={
        <Cluster gap={2}>
          <Button onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          <DeleteRecordButton
            capability="crm.agreements"
            recordLabel={agreement.agreement_number || "this agreement"}
            onDeleted={async () => {
              try {
                await crmApi.deleteAgreement(agreement.id, orgId);
                navigate(crmHref("/agreements"));
              } catch (err) {
                setError(err instanceof Error ? err.message : "Agreement could not be deleted.");
              }
            }}
          />
        </Cluster>
      }
      tabs={[{ key: "edit", label: "Edit" }]}
      activeTab="edit"
      onTabChange={() => {}}
    >
      {error && <p className="crm-error">{error}</p>}
      <Stack gap={4}>
        <DetailSection title="Agreement details">
          <FieldItem label="Company" value={agreement.company_id ? <Link to={crmHref(`/companies/${agreement.company_id}`)}>{agreement.company_name}</Link> : agreement.company_name} />
          <FieldItem label="Agreement number" value={agreement.agreement_number} />
        </DetailSection>
        <RecordAuditInfo record={agreement} />
        {canAssignTerritory && territories.length > 0 ? (
          <Select
            label="Territory"
            tooltip="Sales territory this agreement's price book applies to — used for ownership and reporting."
            value={territoryId}
            onChange={(e) => setTerritoryId(e.target.value)}
          >
            {territories.map((territory) => (
              <option key={territory.id} value={territory.id}>{territory.name}</option>
            ))}
          </Select>
        ) : null}
        <Select label="Status" tooltip={AGREEMENT_STATUS_HINT} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </Select>
        <div className="crm-field-grid">
          <Input label="Start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Input label="End date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <Input label="Payment terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
        <Input label="Delivery terms" value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)} />
        <label className="crm-field">
          <span>Notes</span>
          <textarea className="crm-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </label>
        <DetailSection title="Agreement lines">
          <table className="crm-table crm-agreement-lines-table">
            <thead>
              <tr>
                <th>Line ID</th>
                <th>Product</th>
                <th>Unit price</th>
                <th>Currency</th>
                <th>Discount %</th>
                <th>UOM</th>
                <th>Min</th>
                <th>Max</th>
                <th>Lead time</th>
                <th>Modified by</th>
                <th>Modified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.id ?? index}>
                  <td>{line.line_code ?? "New"}</td>
                  <td>
                    <Select
                      aria-label="Product"
                      value={line.product_id}
                      onChange={(e) => {
                        const product = products.find((item) => item.id === e.target.value);
                        setLines((current) =>
                          current.map((row, rowIndex) =>
                            rowIndex === index
                              ? {
                                  ...row,
                                  product_id: e.target.value,
                                  contracted_unit_price: product?.unit_price ?? row.contracted_unit_price,
                                  currency: product?.currency ?? row.currency,
                                  unit_of_measure: product?.unit_of_measure ?? row.unit_of_measure ?? "each",
                                }
                              : row,
                          ),
                        );
                      }}
                    >
                      {productOptionsFor(products, line).map((product) => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </Select>
                  </td>
                  <td><Input type="number" value={String(line.contracted_unit_price ?? "")} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, contracted_unit_price: e.target.value ? Number(e.target.value) : null } : row))} /></td>
                  <td><Input aria-label="Line currency" value={line.currency ?? ""} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, currency: e.target.value.toUpperCase() } : row))} /></td>
                  <td><Input type="number" value={String(line.discount_pct ?? "")} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, discount_pct: e.target.value ? Number(e.target.value) : null } : row))} /></td>
                  <td><Input value={line.unit_of_measure ?? "each"} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, unit_of_measure: e.target.value } : row))} /></td>
                  <td><Input type="number" value={String(line.min_qty ?? "")} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, min_qty: e.target.value ? Number(e.target.value) : null } : row))} /></td>
                  <td><Input type="number" value={String(line.max_qty ?? "")} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, max_qty: e.target.value ? Number(e.target.value) : null } : row))} /></td>
                  <td><Input type="number" value={String(line.lead_time_days ?? "")} onChange={(e) => setLines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, lead_time_days: e.target.value ? Number(e.target.value) : null } : row))} /></td>
                  <td>{line.id ? stampUser(line.updated_by_email ?? line.created_by_email) : "New"}</td>
                  <td>{line.id ? formatDateTime(line.updated_at ?? line.created_at) : "—"}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => setLines((current) => current.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Cluster className="crm-agreement-lines-actions" gap={2}>
            <Button variant="secondary" onClick={addLine} disabled={products.length === 0}>Add line</Button>
          </Cluster>
        </DetailSection>
        <div className="crm-form-actions">
          <Button onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </Stack>
    </RecordWorkspace>
  );
}

export function MarketingPage({ orgId }: { orgId: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setCampaigns((await opsApi.listCampaigns(orgId)).campaigns);
      setAudience((await opsApi.campaignAudience(orgId)).count);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Campaigns could not be loaded.");
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    setError(null);
    try {
      await opsApi.createCampaign(orgId, { name, subject, body });
      setName("");
      setSubject("");
      setBody("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Campaign could not be saved.");
    }
  }

  return (
    <ListWorkspace title="Marketing">
      {error && <p className="crm-error">{error}</p>}
      <p className="home-subtitle">Audience is contacts with marketing enabled. Send uses SMTP when configured, otherwise a local sink so you can still test tracking.</p>
      <p>Eligible contacts: {audience}</p>
      <Card>
        <Stack gap={3}>
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Campaign name" />
          <Input value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Subject" />
          <Input value={body} onChange={(event) => setBody(event.target.value)} placeholder="Body" />
          {name.trim() && campaigns.some((item) => item.name.trim().toLowerCase() === name.trim().toLowerCase()) ? (
            <p className="crm-warning">A campaign with this name already exists. You can still save it.</p>
          ) : null}
          <div className="crm-form-actions">
            <Button onClick={() => create()} disabled={!name || !subject || !body}>Save draft</Button>
          </div>
        </Stack>
      </Card>
      <table className="crm-table">
        <thead>
          <tr><th>Name</th><th>Status</th><th>Audience</th><th className="crm-table-actions-col">Actions</th></tr>
        </thead>
        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.id}>
              <td>{campaign.name}</td>
              <td>{campaign.status}</td>
              <td>{campaign.audience?.count ?? "—"}</td>
              <td className="crm-table-actions">
                <ResponsiveActions
                  primary={
                    campaign.status !== "completed" && campaign.status !== "sending" ? (
                      <Button variant="secondary" onClick={() => opsApi.sendCampaign(orgId, campaign.id).then(load).catch((err) => setError(err instanceof Error ? err.message : "Send failed"))}>
                        Send
                      </Button>
                    ) : null
                  }
                  more={
                    <DeleteRecordButton
                      capability="crm.marketing"
                      recordLabel={campaign.name}
                      size="sm"
                      variant="ghost"
                      onDeleted={async () => {
                        await opsApi.deleteCampaign(orgId, campaign.id);
                        await load();
                      }}
                    />
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListWorkspace>
  );
}

function CurrencyFigures({
  amounts, field, single, formatMoney,
}: {
  amounts?: ForecastAmount[];
  field: "weighted" | "unweighted";
  single: number | string | null | undefined;
  formatMoney: (value: number | string | null | undefined, currency?: string | null, exact?: boolean) => string;
}) {
  const rows = (amounts ?? []).filter((item) => item.currency);
  if (rows.length <= 1) return <>{formatMoney(single, rows[0]?.currency, true)}</>;
  return (
    <Stack gap={1}>
      {rows.map((row) => (
        <span key={`${row.currency}-${field}`}>{formatMoney(row[field], row.currency, true)}</span>
      ))}
    </Stack>
  );
}

function quotaOnPlan(person: ForecastPerson): boolean {
  const cadence = person.target_cadence ?? "year";
  return Boolean(person.plan_status) && (cadence === "month" || cadence === "quarter");
}

function quotaConfirmed(person: ForecastPerson): boolean {
  return person.plan_status === "agreed" || person.plan_status === "issued" || person.plan_status === "closed";
}

export function ForecastPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { session } = useAuth();
  const { can } = useAccess();
  const { formatMoney, currency: orgCurrency } = useLocale();
  const money = (value: number | string | null | undefined, currency?: string | null) => formatMoney(value, currency, true);
  const canEdit = Boolean(session?.isOrgAdmin)
    || can("crm.forecast", "configure")
    || can("crm.targets", "configure")
    || can("crm.companies", "delete");
  const thisYear = new Date().getFullYear();
  const [params, setParams] = useSearchParams();
  const [year, setYear] = useState(thisYear);
  const [tab, setTab] = useState<"targets" | "plan" | "sales">("targets");
  const [view, setView] = useState<"rollup" | "detail">("rollup");
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const territoryId = params.get("territory") || "all";
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [selectedUser, setSelectedUser] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const forecastSeq = useRef(0);
  const resetSalesperson = useRef(false);

  const load = useCallback(async () => {
    const seq = ++forecastSeq.current;
    const dropSelection = resetSalesperson.current;
    resetSalesperson.current = false;
    try {
      const row = await opsApi.forecast(orgId, year, view, territoryId);
      if (seq !== forecastSeq.current) return;
      setForecast(row);
      const next: Record<string, string> = {};
      for (const person of row.people ?? []) {
        next[person.user_id] = Number(person.yearly_target) ? String(Number(person.yearly_target)) : "";
      }
      for (const quota of row.territory_targets ?? []) {
        next[`t:${quota.territory_id}`] = Number(quota.yearly_target) ? String(Number(quota.yearly_target)) : "";
      }
      setDrafts(next);
      setSelectedUser((current) => {
        if (dropSelection) {
          if (current && row.people?.some((person) => person.user_id === current)) return current;
          return "";
        }
        if (current && row.people?.some((person) => person.user_id === current)) return current;
        if (session?.userId && row.people?.some((person) => person.user_id === session.userId)) return session.userId;
        return row.people?.[0]?.user_id ?? "";
      });
    } catch (err) {
      if (seq !== forecastSeq.current) return;
      setError(err instanceof Error ? err.message : "Forecast could not be loaded.");
    }
  }, [orgId, year, session?.userId, view, territoryId]);

  useEffect(() => {
    void opsApi.listTerritories(orgId).then((row) => setTerritories(row.territories)).catch(() => setTerritories([]));
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveYearly(person: ForecastPerson) {
    const amount = drafts[person.user_id] || "0";
    setSaving(person.user_id);
    setError(null);
    try {
      await opsApi.createTarget(orgId, {
        subject_type: "user",
        subject_id: person.user_id,
        period_start: forecast?.period_start || `${year}-01-01`,
        period_end: forecast?.period_end || `${year}-12-31`,
        currency: orgCurrency || "AUD",
        amount,
      });
      reportSaved(show);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Target could not be saved."));
    } finally {
      setSaving(null);
    }
  }

  async function saveTerritory(territory: Territory) {
    const key = `t:${territory.id}`;
    setSaving(key);
    setError(null);
    try {
      await opsApi.createTarget(orgId, {
        subject_type: "territory",
        subject_id: territory.id,
        period_start: forecast?.period_start || `${year}-01-01`,
        period_end: forecast?.period_end || `${year}-12-31`,
        currency: orgCurrency || "AUD",
        amount: drafts[key] || "0",
      });
      reportSaved(show);
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Territory quota could not be saved."));
    } finally {
      setSaving(null);
    }
  }

  async function clearTerritory(territory: Territory) {
    const key = `t:${territory.id}`;
    setSaving(key);
    setError(null);
    try {
      await opsApi.deleteTargets(orgId, {
        subject_type: "territory",
        subject_id: territory.id,
        period_start: forecast?.period_start || `${year}-01-01`,
        period_end: forecast?.period_end || `${year}-12-31`,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Territory quota could not be deleted.");
    } finally {
      setSaving(null);
    }
  }

  async function clearYearly(person: ForecastPerson) {
    setSaving(person.user_id);
    setError(null);
    try {
      await opsApi.deleteTargets(orgId, {
        subject_type: "user",
        subject_id: person.user_id,
        period_start: forecast?.period_start || `${year}-01-01`,
        period_end: forecast?.period_end || `${year}-12-31`,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Target could not be deleted.");
    } finally {
      setSaving(null);
    }
  }

  const selected = forecast?.people?.find((person) => person.user_id === selectedUser);
  const yearChoices = Array.from({ length: 7 }, (_, index) => thisYear - 3 + index);
  const quotaTerritories = territories;
  const showingRollup = Boolean(forecast?.can_rollup && (forecast.view ?? view) === "rollup");

  function setTerritoryId(value: string) {
    const normalized = !value || value === "all" ? "all" : value;
    if (normalized !== territoryId) {
      resetSalesperson.current = true;
      setSelectedUser("");
    }
    const next = new URLSearchParams(params);
    if (normalized === "all") next.delete("territory");
    else next.set("territory", normalized);
    setParams(next, { replace: true });
  }

  return (
    <ListWorkspace
      title="Forecast & KPI"
      actions={
        <Cluster gap={2} align="center">
          <Tooltip
            text="Year for these targets and for the sales plan. The plan starts in the fiscal month set for the selected territory in Organization settings, unless you pick another month on the Sales plan tab."
            label="About year"
          >
            <span className="home-kpi-label">Year</span>
          </Tooltip>
          <Select aria-label="Year" value={String(year)} onChange={(event) => setYear(Number(event.target.value))}>
            {yearChoices.map((choice) => (
              <option key={choice} value={choice}>{choice}</option>
            ))}
          </Select>
          {territories.some((item) => item.code !== "global") ? (
            <Tooltip
              text="Territory filter for targets and for whose plan you are editing. Changing it clears the salesperson on Sales plan, so you pick someone assigned to the new territory. The filter stays selected when you change tabs or reload. Saving a plan does not change who belongs to a territory."
              label="About territory"
            >
              <span className="home-kpi-label">Territory</span>
            </Tooltip>
          ) : null}
          {territories.some((item) => item.code !== "global") ? (
            <Select aria-label="Territory" value={territoryId} onChange={(event) => setTerritoryId(event.target.value)}>
              <option value="all">All territories</option>
              {territories.map((territory) => (
                <option key={territory.id} value={territory.id}>{territory.name}</option>
              ))}
            </Select>
          ) : null}
        </Cluster>
      }
    >
      <Tabs
        items={[
          { key: "targets", label: "Targets", dataTour: "forecast-tab-targets" },
          { key: "plan", label: "Sales plan", dataTour: "forecast-tab-plan" },
          { key: "sales", label: "Sales dashboard", dataTour: "forecast-tab-sales" },
        ]}
        activeKey={tab}
        onChange={(key) => setTab(key as "targets" | "plan" | "sales")}
      />
      {tab === "sales" ? (
        <ForecastSalesDashboard orgId={orgId} />
      ) : tab === "plan" ? (
        forecast ? (
          <SalesPlanPanel
            orgId={orgId}
            year={year}
            people={forecast.people ?? []}
            selectedUser={selectedUser}
            onSelectUser={setSelectedUser}
            canEdit={canEdit}
            territoryId={territoryId}
            territories={territories}
            sessionUserId={session?.userId}
          />
        ) : (
          <Spinner />
        )
      ) : (
        <>
      {error && <p className="crm-error">{error}</p>}
      {forecast && (
        <Stack gap={5}>
          <p className="home-subtitle">{forecast.basis}</p>
          {(forecast.currency_totals?.length ?? 0) > 1 && territoryId === "all" && (
            <p className="home-subtitle">Each currency is shown on its own. These figures are not added together.</p>
          )}
          <Cluster gap={4}>
            <Card>
              <Tooltip text="Open pipeline multiplied by each deal's win probability. This is the expected value if today's odds hold.">
                <div className="home-kpi-label">Weighted</div>
              </Tooltip>
              <div className="home-kpi-value">
                <CurrencyFigures amounts={forecast.currency_totals} field="weighted" single={forecast.weighted_total} formatMoney={formatMoney} />
              </div>
            </Card>
            <Card>
              <Tooltip text="Full face value of open deals, with no probability applied. Same figure as Open pipeline on the home dashboard.">
                <div className="home-kpi-label">Unweighted</div>
              </Tooltip>
              <div className="home-kpi-value">
                <CurrencyFigures amounts={forecast.currency_totals} field="unweighted" single={forecast.unweighted_total} formatMoney={formatMoney} />
              </div>
            </Card>
            <Card>
              <Tooltip text="Yearly salesperson quotas plus territory quotas for this fiscal year. A territory quota is added to the quotas of the people in that territory. The home-page gauge uses the current user's quarterly slice.">
                <div className="home-kpi-label">Target</div>
              </Tooltip>
              <div className="home-kpi-value">{money(forecast.target_total)}</div>
            </Card>
            <Card>
              <Tooltip text="Open deals the owner has marked as commit — expected to close this period.">
                <div className="home-kpi-label">Commit</div>
              </Tooltip>
              <div className="home-kpi-value">
                <CurrencyFigures amounts={forecast.categories?.commit.amounts} field="weighted" single={forecast.categories?.commit.weighted} formatMoney={formatMoney} />
              </div>
            </Card>
            <Card>
              <Tooltip text="Open deals marked upside — possible, but not yet committed.">
                <div className="home-kpi-label">Upside</div>
              </Tooltip>
              <div className="home-kpi-value">
                <CurrencyFigures amounts={forecast.categories?.upside.amounts} field="weighted" single={forecast.categories?.upside.weighted} formatMoney={formatMoney} />
              </div>
            </Card>
            <Card>
              <Tooltip text="Remaining weighted pipeline that is neither commit nor upside.">
                <div className="home-kpi-label">Pipeline</div>
              </Tooltip>
              <div className="home-kpi-value">
                <CurrencyFigures amounts={forecast.categories?.pipeline.amounts} field="weighted" single={forecast.categories?.pipeline.weighted} formatMoney={formatMoney} />
              </div>
            </Card>
          </Cluster>
          {(() => {
            const person = (forecast.people ?? []).find((row) => row.user_id === selectedUser) ?? forecast.people?.[0];
            if (!person) return null;
            const onPlan = quotaOnPlan(person);
            return (
              <Card>
                <Stack gap={3}>
                  <h2 className="home-section-title">Yearly quota · {year}</h2>
                  <p className="home-subtitle">
                    {person.display_name}: {Number(person.yearly_target) ? money(person.yearly_target) : "No quota saved yet."}
                    {quotaConfirmed(person) ? " · Confirmed" : ""}
                  </p>
                  {onPlan ? (
                    <Button onClick={() => setTab("plan")}>Edit on sales plan</Button>
                  ) : canEdit ? (
                    <Cluster gap={2}>
                      <Input
                        aria-label={`Yearly quota for ${person.display_name}`}
                        value={drafts[person.user_id] ?? ""}
                        onChange={(event) => setDrafts({ ...drafts, [person.user_id]: event.target.value })}
                      />
                      <Button onClick={() => void saveYearly(person)} disabled={saving === person.user_id}>
                        Save quota
                      </Button>
                    </Cluster>
                  ) : (
                    <p className="home-subtitle">A manager with forecast access can enter this quota.</p>
                  )}
                </Stack>
              </Card>
            );
          })()}
          {quotaTerritories.length > 0 && (
            <div className="permission-table-wrap">
              <h2 className="home-section-title">Territory quotas · {year}</h2>
              <p className="home-subtitle">Type a yearly amount for a territory, then Save. It is added to the quotas of the people in that territory.</p>
              <table className="crm-table">
                <thead>
                  <tr>
                    <ForecastHeader label="Territory" hint="Territory this quota belongs to." />
                    <ForecastHeader label="Yearly" hint="Editable territory quota for the year selected above." />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {quotaTerritories.map((territory) => {
                    const key = `t:${territory.id}`;
                    const savedRow = (forecast.territory_targets ?? []).find((row) => row.territory_id === territory.id);
                    return (
                      <tr key={territory.id}>
                        <td>{territory.name}</td>
                        <td>
                          <Input
                            type="number"
                            min={0}
                            step="1000"
                            disabled={!canEdit}
                            aria-label={`Yearly quota for ${territory.name}`}
                            value={drafts[key] ?? ""}
                            onChange={(event) => setDrafts({ ...drafts, [key]: event.target.value })}
                          />
                        </td>
                        <td>
                          {canEdit && (
                            <Cluster gap={2}>
                              <Button type="button" size="sm" disabled={saving === key} onClick={() => saveTerritory(territory)}>
                                {saving === key ? "Saving…" : "Save"}
                              </Button>
                              {savedRow && (
                                <Button type="button" size="sm" variant="ghost" disabled={saving === key} onClick={() => clearTerritory(territory)}>
                                  Delete
                                </Button>
                              )}
                            </Cluster>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <table className="crm-table">
            <thead>
              <tr>
                <ForecastHeader label="Stage" hint="The current pipeline stage assigned to each open opportunity." />
                <ForecastHeader label="Deals" hint="Number of open opportunities currently in this stage." />
                <ForecastHeader label="Unweighted" hint="Full face value of the open deals in this stage, before win probability is applied." />
                <ForecastHeader label="Weighted" hint="Expected value for this stage: each deal value multiplied by its win probability." />
              </tr>
            </thead>
            <tbody>
              {forecast.pipeline.map((row) => (
                <tr key={row.stage}>
                  <td>{row.stage}</td>
                  <td>{row.deals}</td>
                  <td>
                    <CurrencyFigures amounts={row.amounts} field="unweighted" single={row.unweighted} formatMoney={formatMoney} />
                  </td>
                  <td>
                    <CurrencyFigures amounts={row.amounts} field="weighted" single={row.weighted} formatMoney={formatMoney} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Stack gap={3}>
            <Cluster gap={3} align="center">
              <h2 className="home-section-title">Salesperson quotas · {year}</h2>
              {forecast.can_rollup && (
                <Select
                  aria-label="Forecast view"
                  value={forecast.view ?? view}
                  onChange={(event) => setView(event.target.value as "rollup" | "detail")}
                >
                  <option value="rollup">Rolled up by manager</option>
                  <option value="detail">People detail</option>
                </Select>
              )}
            </Cluster>
            <p className="home-subtitle">
              Choose the year above. A yearly amount splits evenly across quarters and months. A sales plan sets each month or quarter, and forecast uses those amounts.
            </p>
            {showingRollup && (
              <ForecastRollup
                teams={forecast.teams ?? []}
                onOpenDetail={(userId) => {
                  setSelectedUser(userId);
                  setView("detail");
                }}
              />
            )}
            {(forecast.people ?? []).length === 0 ? (
              <EmptyState
                title={territoryId !== "all" ? "No people in this territory" : "No people in this organization"}
                description={territoryId !== "all"
                  ? "Assign people to this territory under Settings, or set a territory quota. Deals are limited to companies in the territory."
                  : "Add people under Settings → Users, then come back to assign targets."}
              />
            ) : (
              <div className="permission-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <ForecastHeader label="Person" hint="Salesperson whose owned opportunities and target are shown on this row." />
                      <ForecastHeader label="Yearly" hint="Editable annual sales target. Saving it recalculates the quarterly and monthly target values." />
                      <ForecastHeader label="Q1" hint="One quarter of the annual target." />
                      <ForecastHeader label="Q2" hint="One quarter of the annual target." />
                      <ForecastHeader label="Q3" hint="One quarter of the annual target." />
                      <ForecastHeader label="Q4" hint="One quarter of the annual target." />
                      <ForecastHeader label="Monthly" hint="One twelfth of the annual target, used by the monthly tracking chart." />
                      <ForecastHeader label="Commit" hint="Probability-weighted value of open deals marked Commit." />
                      <ForecastHeader label="Upside" hint="Probability-weighted value of open deals marked Upside." />
                      <ForecastHeader label="Pipeline" hint="Probability-weighted value of the remaining open pipeline." />
                      <ForecastHeader label="YTD won" hint="Value of closed-won opportunities in the year to date." />
                      <ForecastHeader label="Pace" hint="Year-to-date won revenue as a percentage of the prorated annual target." />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(forecast.people ?? []).map((person) => {
                      const onPlan = quotaOnPlan(person);
                      const yearly = Number(drafts[person.user_id] || 0);
                      const quarterTarget = (quarter: number) => {
                        if (onPlan) {
                          return Number(person.quarters.find((item) => item.quarter === quarter)?.target || 0);
                        }
                        return yearly / 4;
                      };
                      return (
                        <tr key={person.user_id}>
                          <td>
                            <button type="button" className="forecast-person-link" onClick={() => setSelectedUser(person.user_id)}>
                              {person.display_name}
                              {quotaConfirmed(person) ? " · Confirmed" : ""}
                            </button>
                          </td>
                          <td>
                            {onPlan ? (
                              money(person.yearly_target)
                            ) : (
                              <Input
                                type="number"
                                min={0}
                                step="1000"
                                disabled={!canEdit}
                                aria-label={`Yearly target for ${person.display_name}`}
                                value={drafts[person.user_id] ?? ""}
                                onChange={(event) => setDrafts({ ...drafts, [person.user_id]: event.target.value })}
                              />
                            )}
                          </td>
                          <td>{money(quarterTarget(1))}</td>
                          <td>{money(quarterTarget(2))}</td>
                          <td>{money(quarterTarget(3))}</td>
                          <td>{money(quarterTarget(4))}</td>
                          <td>{onPlan ? "On sales plan" : money(yearly / 12)}</td>
                          <td>{money(person.commit_weighted)}</td>
                          <td>{money(person.upside_weighted)}</td>
                          <td>{money(person.pipeline_weighted)}</td>
                          <td>{money(person.ytd_won)}</td>
                          <td>{person.pct.toFixed(0)}%</td>
                          <td>
                            {onPlan ? (
                              <Button type="button" size="sm" variant="secondary" onClick={() => {
                                setSelectedUser(person.user_id);
                                setTab("plan");
                              }}>
                                Sales plan
                              </Button>
                            ) : canEdit && (
                              <Cluster gap={2}>
                                <Button type="button" size="sm" disabled={saving === person.user_id} onClick={() => saveYearly(person)}>
                                  {saving === person.user_id ? "Saving…" : "Save"}
                                </Button>
                                <Button type="button" size="sm" variant="ghost" disabled={saving === person.user_id} onClick={() => clearYearly(person)}>
                                  Delete
                                </Button>
                              </Cluster>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Stack>

          {selected && (
            <Card>
              <Stack gap={3}>
                <h2 className="home-section-title">Tracking · {selected.display_name}</h2>
                <p className="home-subtitle">
                  {(selected.target_cadence ?? "year") === "month"
                    ? "Monthly closed-won versus the sales target saved on the sales plan for that month."
                    : (selected.target_cadence ?? "year") === "quarter"
                      ? "Monthly closed-won. The sales target is set by quarter on the sales plan, so this chart does not draw a monthly line."
                      : "Monthly closed-won versus an even 1/12 of the yearly target. Bars at or above the target line mean that month is on pace."}
                </p>
                <TrackingChart person={selected} />
              </Stack>
            </Card>
          )}
        </Stack>
      )}
        </>
      )}
    </ListWorkspace>
  );
}

function ForecastSalesDashboard({ orgId }: { orgId: string }) {
  const [template, setTemplate] = useState<DashboardTemplate | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    void (async () => {
      try {
        await opsApi.listDashboards(orgId);
        const templates = await dashboardDesignerApi.list(orgId);
        if (cancelled) return;
        const preferred =
          templates.find((row) => row.is_default) ||
          templates.find((row) => row.kind === "starter") ||
          templates.find((row) => row.name === "Sales overview") ||
          templates[0] ||
          null;
        setTemplate(preferred);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Sales dashboard could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (loading) return <Spinner />;
  if (error) return <p className="crm-error" role="alert">{error}</p>;
  if (!template) {
    return (
      <EmptyState
        title="No sales dashboard yet"
        description="The default Sales overview will appear here once CRM dashboards are available for this organisation."
      />
    );
  }
  const layout = normalizeDashboardLayout(template.layout_json, template.name);
  return (
    <DashboardRenderer
      orgId={orgId}
      dashboardId={template.id}
      layout={layout}
      client={dashboardDesignerApi}
    />
  );
}

function ForecastRollup({
  teams,
  onOpenDetail,
}: {
  teams: ForecastTeam[];
  onOpenDetail: (userId: string) => void;
}) {
  const { formatMoney } = useLocale();
  const money = (value: number | string | null | undefined) => formatMoney(value, undefined, true);
  if (teams.length === 0) {
    return <EmptyState title="No reporting lines yet" description="Set reports-to under Settings → Users, then managers can roll up their team here." />;
  }
  return (
    <div className="permission-table-wrap">
      <table className="crm-table">
        <thead>
          <tr>
            <ForecastHeader label="Team" hint="Manager and all salespeople who report through that manager." />
            <ForecastHeader label="People" hint="Number of people included in this manager rollup." />
            <ForecastHeader label="Yearly" hint="Combined annual targets for everyone in the team." />
            <ForecastHeader label="Commit" hint="Combined probability-weighted value of team deals marked Commit." />
            <ForecastHeader label="Upside" hint="Combined probability-weighted value of team deals marked Upside." />
            <ForecastHeader label="Pipeline" hint="Combined probability-weighted value of the team's remaining open pipeline." />
            <ForecastHeader label="YTD won" hint="Combined value of the team's closed-won opportunities this year." />
            <ForecastHeader label="Pace" hint="Team year-to-date won revenue as a percentage of its prorated annual target." />
            <th></th>
          </tr>
        </thead>
        <tbody>
          {teams.map((team) => (
            <tr key={team.manager_user_id ?? "unassigned"}>
              <td>{team.display_name}</td>
              <td>{team.member_count}</td>
              <td>{money(team.yearly_target)}</td>
              <td>{money(team.commit_weighted)}</td>
              <td>{money(team.upside_weighted)}</td>
              <td>{money(team.pipeline_weighted)}</td>
              <td>{money(team.ytd_won)}</td>
              <td>{team.pct.toFixed(0)}%</td>
              <td>
                {team.manager_user_id && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => onOpenDetail(team.manager_user_id!)}>
                    See detail
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ForecastHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <th>
      <Tooltip text={hint} label={`About ${label}`}>
        <span>{label}</span>
      </Tooltip>
    </th>
  );
}

function chartMoney(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs)}`;
}

function TrackingChart({ person }: { person: ForecastPerson }) {
  const plotTop = 24;
  const plotBottom = 84;
  const plotH = plotBottom - plotTop;
  const max = Math.max(
    ...person.months.map((month) => Math.max(Number(month.target) || 0, Number(month.won) || 0)),
    1,
  );
  const targets = person.months.map((month) => Number(month.target) || 0);
  const uniform = targets.every((amount) => amount === targets[0]);
  const target = uniform ? targets[0] || 0 : 0;
  const targetY = plotBottom - (target / max) * plotH;
  const slot = 228 / Math.max(person.months.length, 1);
  const barW = slot * 0.55;
  return (
    <div className="forecast-chart-wrap">
      <svg viewBox="0 0 240 116" className="forecast-chart" role="img" aria-label={`Monthly tracking for ${person.display_name}`}>
        {target > 0 && (
          <line x1="8" y1={targetY} x2="232" y2={targetY} className="forecast-chart-target-line" />
        )}
        {person.months.map((month, index) => {
          const won = Number(month.won) || 0;
          const monthTarget = Number(month.target) || 0;
          const wonH = Math.max((won / max) * plotH, 2);
          const x = 12 + index * slot + (slot - barW) / 2;
          const onPace = won >= monthTarget && monthTarget > 0;
          return (
            <g key={month.month}>
              <title>{`${month.label}: won ${chartMoney(won)} / target ${chartMoney(monthTarget)}`}</title>
              <rect
                x={x}
                y={plotBottom - wonH}
                width={barW}
                height={wonH}
                rx="1.5"
                className={
                  won > 0
                    ? onPace
                      ? "forecast-chart-won"
                      : "forecast-chart-won is-behind"
                    : "forecast-chart-won is-empty"
                }
              />
              {won > 0 && (
                <text x={x + barW / 2} y={plotBottom - wonH - 4} textAnchor="middle" className="forecast-chart-amount" fontSize="13">
                  {chartMoney(won)}
                </text>
              )}
              <text x={x + barW / 2} y="102" textAnchor="middle" className="forecast-chart-label" fontSize="6">
                {month.label.slice(0, 3)}
              </text>
            </g>
          );
        })}
      </svg>
      <ul className="forecast-chart-legend-row">
        <li>
          <span className="forecast-chart-swatch forecast-chart-swatch--won" />
          Won
        </li>
        <li>
          <span className="forecast-chart-swatch forecast-chart-swatch--target" />
          Target
        </li>
      </ul>
    </div>
  );
}
